import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../api";
import QRScanner from "../components/QRScanner";
import { InStoreSaleForm } from "../components/InStoreSaleForm";
import { Loader2, ScanLine, Search, LogOut, Delete, AlertTriangle, CheckCircle2 } from "lucide-react";

const KEY = "gp_staff_token";
const staffApi = axios.create({ baseURL: API });
staffApi.interceptors.request.use((c) => { const t = localStorage.getItem(KEY); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });
staffApi.interceptors.response.use((r) => r, (e) => {
  if (e?.response?.status === 401 && !e.config.url.includes("/team/login") && localStorage.getItem(KEY)) {
    localStorage.removeItem(KEY); window.location.reload();
  }
  return Promise.reject(e);
});

const PinPad = ({ slug, onIn }) => {
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true); setErr("");
    try { const { data } = await staffApi.post("/team/login", { slug, pin }); localStorage.setItem(KEY, data.token); onIn(); }
    catch (e) { setErr(e?.response?.data?.detail || "Sign in failed"); setPin(""); } finally { setBusy(false); }
  };
  const press = (d) => setPin((p) => (p.length < 8 ? p + d : p));
  return (
    <div className="min-h-screen bg-[#16243b] flex flex-col items-center justify-center px-6 text-white" data-testid="staff-pin-screen">
      <p className="text-[#2ec9c9] text-sm font-semibold">Staff sign in</p>
      <div className="flex gap-3 my-6 h-4" data-testid="staff-pin-dots">{[...Array(Math.max(4, pin.length))].map((_, i) => <span key={i} className={`w-3.5 h-3.5 rounded-full ${i < pin.length ? "bg-[#2ec9c9]" : "bg-white/20"}`} />)}</div>
      <div className="grid grid-cols-3 gap-3 w-full max-w-[280px]">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <button key={d} onClick={() => press(d)} data-testid={`pin-key-${d}`} className="h-16 rounded-2xl bg-white/10 text-2xl font-semibold active:bg-white/25 transition-colors">{d}</button>
        ))}
        <button onClick={() => setPin(pin.slice(0, -1))} data-testid="pin-key-back" className="h-16 rounded-2xl flex items-center justify-center text-white/70"><Delete /></button>
        <button onClick={() => press("0")} data-testid="pin-key-0" className="h-16 rounded-2xl bg-white/10 text-2xl font-semibold active:bg-white/25">0</button>
        <button disabled={pin.length < 4 || busy} onClick={go} data-testid="pin-submit" className="h-16 rounded-2xl bg-[#2ec9c9] font-semibold disabled:opacity-40 flex items-center justify-center">{busy ? <Loader2 className="animate-spin" /> : "Go"}</button>
      </div>
      {err && <p className="text-red-300 text-sm mt-5" data-testid="staff-pin-error">{err}</p>}
    </div>
  );
};

const Voucher = ({ v, onDone }) => {
  const [amount, setAmount] = useState(String(v.balance));
  const [msg, setMsg] = useState(null);
  useEffect(() => { setAmount(String(v.balance)); }, [v.balance]);
  const redeem = async (type, honor_expired = false) => {
    try { const { data } = await staffApi.post("/team/redeem", { code: v.code, amount: parseFloat(amount) || 0, type, honor_expired }); setMsg({ ok: true, text: v.kind === "experience" ? "Experience marked as used." : `Redeemed £${data.redeemed.toFixed(2)}. £${data.balance.toFixed(2)} left on this voucher.` }); onDone(); }
    catch (e) { setMsg({ ok: false, text: e?.response?.status === 409 ? "Expired — tap Honour to accept it anyway." : e?.response?.data?.detail || "Failed", expired: e?.response?.status === 409 }); }
  };
  const used = v.status === "used";
  return (
    <div className="bg-white rounded-2xl p-5 mt-4" data-testid="staff-voucher-card">
      <p className="text-sm text-[#8a97a8]">{v.product_title}{v.recipient_name ? ` · for ${v.recipient_name}` : ""}</p>
      <p className="text-4xl font-bold text-[#16243b] mt-1" data-testid="staff-voucher-balance">{v.kind === "experience" ? (v.balance > 0 ? "1 × experience" : "Experience used") : `£${v.balance.toFixed(2)}`}</p>
      <p className="text-xs text-[#8a97a8]">{v.kind === "experience" ? `£${v.original_amount.toFixed(2)} value` : `of £${v.original_amount.toFixed(2)}`} · <span className="font-mono">{v.code}</span>{v.expires_at ? ` · ${v.expired ? "expired" : "valid until"} ${v.expires_at.slice(0, 10)}` : ""}</p>
      {msg && <p className={`mt-3 text-sm rounded-xl p-3 flex gap-2 ${msg.ok ? "bg-[#eefafa] text-[#1a9c9c]" : "bg-red-50 text-red-600"}`} data-testid="staff-redeem-msg">{msg.ok ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />} {msg.text}</p>}
      {!used && v.kind === "experience" && (
        <button onClick={() => redeem("full", !!msg?.expired)} data-testid="staff-redeem-experience" className="w-full mt-4 gp-btn-teal rounded-xl py-3 font-medium">{msg?.expired ? "Honour & mark used" : "Mark experience as used"}</button>
      )}
      {!used && v.kind !== "experience" && (
        <div className="mt-4 space-y-3">
          <input type="number" step="0.01" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="staff-redeem-amount" className="w-full rounded-xl border border-gray-200 px-4 py-3 text-lg outline-none focus:border-[#2ec9c9]" />
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => redeem("partial", !!msg?.expired)} data-testid="staff-redeem-part" className="rounded-xl py-3 font-medium border-2 border-[#16243b] text-[#16243b] bg-white">{msg?.expired ? "Honour part" : "Take amount"}</button>
            <button onClick={() => redeem("full", !!msg?.expired)} data-testid="staff-redeem-full" className="gp-btn-teal rounded-xl py-3 font-medium">{msg?.expired ? "Honour all" : "Use full balance"}</button>
          </div>
        </div>
      )}
      {used && !msg && <p className="mt-3 text-sm bg-red-50 text-red-600 rounded-xl p-3">Already fully used.</p>}
    </div>
  );
};

const RedeemPanel = () => {
  const [code, setCode] = useState("");
  const [scan, setScan] = useState(false);
  const [v, setV] = useState(null);
  const [err, setErr] = useState("");
  const lookup = useCallback(async (c) => {
    const val = (c || "").trim().toUpperCase(); if (!val) return;
    setCode(val); setErr(""); setV(null);
    try { setV((await staffApi.get(`/team/voucher/${val}`)).data); } catch (e) { setErr(e?.response?.data?.detail || "Not found"); }
  }, []);
  const onScan = useCallback((c) => { setScan(false); lookup(c); }, [lookup]);
  return (
    <div>
      <button onClick={() => setScan(true)} data-testid="staff-scan-btn" className="w-full gp-btn-teal rounded-2xl py-5 text-lg font-semibold flex items-center justify-center gap-3"><ScanLine /> Scan voucher QR</button>
      <form onSubmit={(e) => { e.preventDefault(); lookup(code); }} className="flex gap-2 mt-3">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={10} placeholder="or type the code" data-testid="staff-code-input" className="flex-1 font-mono tracking-wider rounded-xl px-4 py-3 outline-none uppercase" />
        <button className="bg-white/15 text-white rounded-xl px-4" data-testid="staff-code-search"><Search size={18} /></button>
      </form>
      {err && <p className="text-red-300 text-sm mt-3" data-testid="staff-lookup-error">{err}</p>}
      {v && <Voucher key={v.code} v={v} onDone={() => staffApi.get(`/team/voucher/${v.code}`).then(({ data }) => setV(data))} />}
      {scan && <QRScanner onResult={onScan} onClose={() => setScan(false)} />}
    </div>
  );
};

export default function StaffApp() {
  const { slug } = useParams();
  const [me, setMe] = useState(null);
  const [state, setState] = useState("loading");
  const [tab, setTab] = useState("redeem");
  const load = useCallback(() => {
    if (!localStorage.getItem(KEY)) return setState("pin");
    staffApi.get("/team/me").then(({ data }) => { if (data.business.slug !== slug) throw new Error(); setMe(data); setState("in"); })
      .catch(() => { localStorage.removeItem(KEY); setState("pin"); });
  }, [slug]);
  useEffect(() => { load(); }, [load]);
  const out = () => { localStorage.removeItem(KEY); setMe(null); setState("pin"); };

  if (state === "loading") return <div className="min-h-screen bg-[#16243b] flex items-center justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={36} /></div>;
  if (state === "pin") return <PinPad slug={slug} onIn={load} />;
  return (
    <div className="min-h-screen bg-[#16243b] px-5 pb-10" data-testid="staff-app">
      <div className="max-w-[480px] mx-auto">
      <header className="flex items-center justify-between py-5">
        <div className="flex items-center gap-3 min-w-0">
          {me.business.logo_url && <img src={me.business.logo_url} alt="" className="h-9 w-9 rounded-lg object-contain bg-white p-1" />}
          <div className="min-w-0"><p className="text-white font-semibold truncate">{me.business.name}</p><p className="text-white/50 text-xs" data-testid="staff-name">Signed in as {me.member.name}</p></div>
        </div>
        <button onClick={out} data-testid="staff-logout" className="text-white/60 p-2"><LogOut size={20} /></button>
      </header>
      {me.can_sell && (
        <div className="grid grid-cols-2 bg-white/10 rounded-xl p-1 mb-5">
          {[["redeem", "Redeem"], ["sell", "Sell"]].map(([id, l]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`staff-tab-${id}`} className={`rounded-lg py-2.5 text-sm font-semibold transition-colors ${tab === id ? "bg-white text-[#16243b]" : "text-white/70"}`}>{l}</button>
          ))}
        </div>
      )}
      {tab === "redeem" || !me.can_sell ? <RedeemPanel /> : (
        <div className="bg-white rounded-2xl p-5"><InStoreSaleForm products={me.products} onSell={async (p) => (await staffApi.post("/team/sell", p)).data} /></div>
      )}
      </div>
    </div>
  );
}
